import { Page, PageTitle } from "@/components/page"
import { CRUDLoader } from "@/components/page/crudloader"
import { CRUDToolbar } from "@/components/page/crudtoolbar"
import { useDatabaseData, useDatabaseItem, usePage } from "@/components/page/hooks"
import { Combobox } from "@/components/ui/combobox"
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { zodResolver } from "@hookform/resolvers/zod"
import { Route } from "lucide-react"
import { useForm } from "react-hook-form"
import { useOpenRecordContext, useReloadOnAssistantChange } from "@/lib/assistant/modules/channels"
import { z } from "zod"

export const routers_crud = {
  title: "Router",
  route: "/settings/routers/:id",
  component,
  group: "settings",
  resource: "apikeys",
  icon: Route,
  action: "read",
} satisfies PageConfig

const FormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  asset_id: z.string().min(1, "Asset ID is required"),
  channel: z.string({ required_error: "Channel is required" }).min(1, "Channel is required"),
})

type FormData = z.infer<typeof FormSchema>

// The platform is not asked for: the row points at one channel and the channel already declares its type.
const types = {
  whatsapp: "WhatsApp",
  messenger: "Facebook Messenger",
  instagram: "Instagram",
} as const

// What to paste into Asset ID, which is what the webhook carries for the selected channel's platform.
const assetHints = {
  whatsapp: "Phone Number ID of the WhatsApp Business number, not the phone number itself.",
  messenger: "ID of the Facebook Page that receives the messages.",
  instagram: "ID of the Instagram professional account. The channel still needs the linked Page ID to answer.",
} as const

export function component() {
  const { id } = usePage()
  const version = useReloadOnAssistantChange("routers", id, "/settings/routers")
  return <RouterEditor key={version} />
}

function RouterEditor() {

  const { team, id } = usePage()
  const { data, loading, submit, toastSuccess, navigate } = useDatabaseItem("routers")
  const { data: channelsData, loading: channelsLoading } = useDatabaseData("channels", { fields: ["id", "title", "type", "team"] })

  const form = useForm<FormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: data as FormData,
    values: data as FormData,
  })

  const channelId = form.watch("channel")

  useOpenRecordContext("routers", id, () => form.getValues("title"), () => form.formState.isDirty)

  async function onSubmit(data: FormData) {
    const payload = { ...data, team: team?.id }
    await submit(payload as any)
    toastSuccess()
    navigate("/settings/routers")
  }

  if (loading || channelsLoading) return <CRUDLoader />

  const metaChannels = (channelsData || []).filter((c: any) => c.team === team?.id && !!types[c.type])
  const channelOptions = metaChannels.map((c: any) => ({ value: c.id, label: `${c.title} (${types[c.type]})` }))

  // Which id to paste depends on the platform, so the hint follows the channel that was picked.
  const selectedType = metaChannels.find((c: any) => c.id === channelId)?.["type"]

  return <Page>
    <div className="pb-8">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <PageTitle>
            <CRUDToolbar table="routers" onSuccess="/settings/routers" />
          </PageTitle>
          <div className="rgrid">
            <div className="row">
              <div className="rc-6">
                <FormField control={form.control} name="title" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
              <div className="rc-6">
                <FormField control={form.control} name="channel" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Channel</FormLabel>
                    <FormControl>
                      <Combobox items={channelOptions} value={field.value} onChange={field.onChange} placeholder="Search channel..." />
                    </FormControl>
                    <FormDescription>The channel sets the platform and holds the access token. The router fills in the ids it already knows from the payload.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>

            <div className="row">
              <div className="rc-6">
                <FormField control={form.control} name="asset_id" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Asset ID</FormLabel>
                    <FormControl><Input {...field} value={field.value ?? ""} /></FormControl>
                    {assetHints[selectedType] && <FormDescription>{assetHints[selectedType]}</FormDescription>}
                    <FormMessage />
                  </FormItem>
                )} />
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  </Page>

}
